export type DashboardPreset = 'development' | 'support' | 'pilotage' | 'client' | 'custom';

export type DashboardWidgetId =
    | 'sprintInfo'
    | 'briefingKpis'
    | 'favorites'
    | 'myTasks'
    | 'whatsNew'
    | 'lastActivity'
    | 'supportIndicators'
    | 'triageChanges'
    | 'teamReview'
    | 'lastReleases';

export const DASHBOARD_WIDGET_IDS: DashboardWidgetId[] = [
    'sprintInfo',
    'briefingKpis',
    'favorites',
    'myTasks',
    'whatsNew',
    'lastActivity',
    'supportIndicators',
    'triageChanges',
    'teamReview',
    'lastReleases',
];

export type DashboardPreferences = {
    preset: DashboardPreset;
    hiddenWidgets: DashboardWidgetId[];
    hiddenKpis: string[];
};

const PRESET_HIDDEN: Record<Exclude<DashboardPreset, 'custom'>, DashboardWidgetId[]> = {
    // Sprint, KPIs, favorites, tasks, activity, team review, releases — hide support widgets.
    development: ['supportIndicators', 'triageChanges'],
    // Favorites, tasks, activity, support indicators, triage — hide sprint/KPIs/review/releases.
    support: ['sprintInfo', 'briefingKpis', 'teamReview', 'lastReleases'],
    pilotage: ['lastReleases'],
    client: ['sprintInfo', 'briefingKpis', 'supportIndicators', 'triageChanges', 'teamReview', 'lastReleases'],
};

export const DEFAULT_DASHBOARD_PREFERENCES: DashboardPreferences = {
    preset: 'development',
    hiddenWidgets: [...PRESET_HIDDEN.development],
    hiddenKpis: [],
};

function migrateHiddenWidgets(raw: string[] | undefined): DashboardWidgetId[] {
    if (!Array.isArray(raw))
        return [];

    const migrated: string[] = [];
    raw.forEach((id) => {
        // Legacy single "briefing" toggle → hide sprint + KPIs separately.
        if (id === 'briefing') {
            migrated.push('sprintInfo', 'briefingKpis');
            return;
        }
        migrated.push(id);
    });

    return migrated.filter((id): id is DashboardWidgetId =>
        (DASHBOARD_WIDGET_IDS as string[]).includes(id));
}

function normalizePreset(raw?: string): DashboardPreset {
    // Legacy "delivery" → "development"
    if (raw === 'delivery')
        return 'development';
    // Legacy "dispatcher" → "pilotage"
    if (raw === 'dispatcher')
        return 'pilotage';
    if ((['development', 'support', 'pilotage', 'client', 'custom'] as DashboardPreset[]).includes(raw as DashboardPreset))
        return raw as DashboardPreset;
    return 'development';
}

export function normalizeDashboardPreferences(raw?: {
    preset?: string;
    hiddenWidgets?: string[];
    hiddenKpis?: string[];
} | null): DashboardPreferences {
    const preset = normalizePreset(raw?.preset);
    const hiddenWidgets = migrateHiddenWidgets(raw?.hiddenWidgets);

    const hiddenKpis = Array.isArray(raw?.hiddenKpis)
        ? raw!.hiddenKpis.filter((key) => typeof key === 'string')
        : [];

    // Apply preset defaults when still on a named preset with empty/legacy hide lists.
    if (preset !== 'custom') {
        const legacyDeliveryOnly =
            preset === 'development'
            && hiddenWidgets.length === 1
            && hiddenWidgets[0] === 'triageChanges';
        const legacySupport =
            preset === 'support'
            && hiddenWidgets.length === 2
            && hiddenWidgets.includes('lastReleases')
            && hiddenWidgets.includes('teamReview')
            && !hiddenWidgets.includes('sprintInfo');
        const legacyClient =
            preset === 'client'
            && !hiddenWidgets.includes('sprintInfo')
            && hiddenWidgets.includes('supportIndicators')
            && hiddenWidgets.includes('triageChanges');
        if (hiddenWidgets.length === 0 || legacyDeliveryOnly || legacySupport || legacyClient) {
            return {
                preset,
                hiddenWidgets: [...PRESET_HIDDEN[preset]],
                hiddenKpis,
            };
        }
    }

    return { preset, hiddenWidgets, hiddenKpis };
}

export function preferencesForPreset(preset: DashboardPreset): DashboardPreferences {
    if (preset === 'custom') {
        return { preset: 'custom', hiddenWidgets: [], hiddenKpis: [] };
    }
    return {
        preset,
        hiddenWidgets: [...PRESET_HIDDEN[preset]],
        hiddenKpis: [],
    };
}

export function isWidgetVisible(
    prefs: DashboardPreferences,
    widgetId: DashboardWidgetId,
): boolean {
    return !prefs.hiddenWidgets.includes(widgetId);
}

export function toggleWidgetHidden(
    prefs: DashboardPreferences,
    widgetId: DashboardWidgetId,
): DashboardPreferences {
    const hidden = new Set(prefs.hiddenWidgets);
    if (hidden.has(widgetId))
        hidden.delete(widgetId);
    else
        hidden.add(widgetId);

    return {
        preset: 'custom',
        hiddenWidgets: DASHBOARD_WIDGET_IDS.filter((id) => hidden.has(id)),
        hiddenKpis: prefs.hiddenKpis,
    };
}

/** Tab slugs that can be hidden via project posture (boards always visible). */
export const HIDABLE_PROJECT_TABS = [
    'releases',
    'activity',
    'commits',
    'changelog',
    'documentation',
    'attachments',
    'quotes',
    'reporting',
    'analysis',
    'checks',
] as const;

export type HidableProjectTab = typeof HIDABLE_PROJECT_TABS[number];
